import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import WordEditButton, { printDoc } from '../components/WordEditButton';

const TYPES = ['Закон', 'Кодекс', 'Шаблон', 'Стратегия', 'Постановление Пленума ВС', 'Обзор практики ВС', 'Определение ВС', 'Постановление КС',
  'Решение суда', 'Письмо ведомства', 'Иное'];
const emptyForm = (folder = '', subfolder = '') => ({ folder, subfolder, title: '', doc_type: '', doc_date: '', note: '' });

function PrintButton({ d }) {
  const [busy, setBusy] = useState(false);
  if (!/\.docx$/i.test(d.original_name || '')) return null;
  return <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setBusy(true); printDoc('legal', d.id, () => setBusy(false)); }}>{busy ? 'Готовлю…' : '🖨 Печать'}</button>;
}

export default function LegalDocsPage() {
  const [docs, setDocs] = useState([]);
  const [sel, setSel] = useState({ folder: null, subfolder: null });
  const [q, setQ] = useState('');
  const [upForm, setUpForm] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get('/legal-docs').then(r => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const tree = docs.reduce((t, d) => {
    t[d.folder] = t[d.folder] || { count: 0, subs: {} };
    t[d.folder].count++;
    if (d.subfolder) t[d.folder].subs[d.subfolder] = (t[d.folder].subs[d.subfolder] || 0) + 1;
    return t;
  }, {});
  const folders = Object.keys(tree).sort((a, b) => a.localeCompare(b, 'ru'));
  const subsOf = f => Object.keys((tree[f] || {}).subs || {}).sort((a, b) => a.localeCompare(b, 'ru'));

  const needle = q.trim().toLowerCase();
  // поиск по словам в любом порядке и по началу слова: «отказ коллект», «жалоба дтп»
  const words = needle.replace(/ё/g, 'е').split(/\s+/).filter(Boolean);
  const hay = d => [d.title, d.note, d.doc_type, d.folder, d.subfolder, d.original_name].filter(Boolean).join(' ').toLowerCase().replace(/ё/g, 'е');
  const shown = docs.filter(d => needle
    ? words.every(w => hay(d).includes(w))
    : (!sel.folder || d.folder === sel.folder) && (!sel.subfolder || d.subfolder === sel.subfolder));

  const doUpload = async () => {
    const files = fileRef.current?.files;
    if (!files || !files.length) return alert('Выберите файл');
    if (!upForm.folder.trim()) return alert('Укажите папку');
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    Object.entries(upForm).forEach(([k, v]) => fd.append(k, v || ''));
    setBusy(true);
    try { await api.post('/legal-docs', fd); setUpForm(null); setSel({ folder: upForm.folder.trim(), subfolder: upForm.subfolder.trim() || null }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Не удалось загрузить'); }
    setBusy(false);
  };
  const saveEdit = async () => {
    try { await api.put(`/legal-docs/${edit.id}`, edit); setEdit(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Не удалось сохранить'); }
  };
  const remove = async d => { if (window.confirm(`Удалить «${d.title}» из правовой базы?`)) { await api.delete(`/legal-docs/${d.id}`); load(); } };

  const metaFields = (f, set) => (
    <>
      <div className="form-row">
        <div className="form-group"><label>Папка</label><input className="form-input" list="ld-folders" value={f.folder} onChange={e => set({ ...f, folder: e.target.value })} placeholder="Земля, Банкротство, Семья…" /></div>
        <div className="form-group"><label>Подпапка</label><input className="form-input" list="ld-subs" value={f.subfolder || ''} onChange={e => set({ ...f, subfolder: e.target.value })} placeholder="необязательно" /></div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 2 }}><label>Название</label><input className="form-input" value={f.title || ''} onChange={e => set({ ...f, title: e.target.value })} placeholder="если пусто — из имени файла" /></div>
        <div className="form-group"><label>Вид</label><select className="form-select" value={f.doc_type || ''} onChange={e => set({ ...f, doc_type: e.target.value })}><option value="">—</option>{TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
        <div className="form-group"><label>Дата документа</label><input type="date" className="form-input" value={f.doc_date ? String(f.doc_date).slice(0, 10) : ''} onChange={e => set({ ...f, doc_date: e.target.value })} /></div>
      </div>
      <div className="form-group"><label>Заметка (о чём, какие пункты важны)</label><textarea className="form-input" rows={2} value={f.note || ''} onChange={e => set({ ...f, note: e.target.value })} /></div>
      <datalist id="ld-folders">{folders.map(x => <option key={x} value={x} />)}</datalist>
      <datalist id="ld-subs">{subsOf(f.folder).map(x => <option key={x} value={x} />)}</datalist>
    </>
  );

  const item = (on) => ({ padding: '7px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 14, background: on ? 'var(--bg-hover)' : 'transparent', fontWeight: on ? 600 : 400, display: 'flex', justifyContent: 'space-between', gap: 8 });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 className="page-title">Правовая база</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-input" placeholder="Поиск по названию, заметке…" value={q} onChange={e => setQ(e.target.value)} style={{ width: 260 }} />
          {!upForm && <button className="btn btn-primary" onClick={() => setUpForm(emptyForm(sel.folder || '', sel.subfolder || ''))}>+ Загрузить документ</button>}
        </div>
      </div>

      {upForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Загрузить в правовую базу</h3>
          <div className="form-group"><label>Файлы (PDF, Word, RTF, TXT, JSON, фото — до 50 МБ)</label><input type="file" multiple ref={fileRef} accept=".pdf,.doc,.docx,.rtf,.txt,.odt,.xls,.xlsx,.csv,.json,.jpg,.jpeg,.png,.webp" /></div>
          {metaFields(upForm, setUpForm)}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={busy} onClick={doUpload}>{busy ? 'Загрузка…' : 'Загрузить'}</button>
            <button className="btn btn-secondary" onClick={() => setUpForm(null)}>Отмена</button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 760 ? '1fr' : 'minmax(200px, 260px) 1fr', gap: 16, alignItems: 'start' }}>
        <div className="card" style={{ padding: 10 }}>
          <div style={item(!sel.folder && !needle)} onClick={() => { setQ(''); setSel({ folder: null, subfolder: null }); }}><span>Все документы</span><span style={{ color: 'var(--text-muted)' }}>{docs.length}</span></div>
          {folders.map(f => (
            <div key={f}>
              <div style={item(sel.folder === f && !sel.subfolder && !needle)} onClick={() => { setQ(''); setSel({ folder: f, subfolder: null }); }}><span>📁 {f}</span><span style={{ color: 'var(--text-muted)' }}>{tree[f].count}</span></div>
              {subsOf(f).map(s => (
                <div key={s} style={{ ...item(sel.folder === f && sel.subfolder === s && !needle), paddingLeft: 28, fontSize: 13 }} onClick={() => { setQ(''); setSel({ folder: f, subfolder: s }); }}><span>{s}</span><span style={{ color: 'var(--text-muted)' }}>{tree[f].subs[s]}</span></div>
              ))}
            </div>
          ))}
          {!folders.length && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 8 }}>Папок пока нет — загрузите первый документ</div>}
        </div>

        <div>
          {shown.map(d => edit && edit.id === d.id ? (
            <div className="card" key={d.id} style={{ marginBottom: 10 }}>
              {metaFields(edit, setEdit)}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={saveEdit}>Сохранить</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEdit(null)}>Отмена</button>
              </div>
            </div>
          ) : (
            <div className="card" key={d.id} style={{ marginBottom: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <a href={`/api/legal-docs/${d.id}/file`} target="_blank" rel="noreferrer" style={{ fontWeight: 600, fontSize: 15 }}>{d.title}</a>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                    {[d.doc_type, d.doc_date && formatDate(d.doc_date), `${d.folder}${d.subfolder ? ' → ' + d.subfolder : ''}`].filter(Boolean).join(' · ')}
                  </div>
                  {d.note && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, whiteSpace: 'pre-wrap' }}>{d.note}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <WordEditButton type="legal" f={d} showToast={(m, t) => t === 'error' && alert(m)} onSaved={load} />
                  <PrintButton d={d} />
                  <a className="btn btn-secondary btn-sm" href={`/api/legal-docs/${d.id}/file?download=1`}>Скачать</a>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEdit({ ...d })}>Изменить</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => remove(d)}>Удалить</button>
                </div>
              </div>
            </div>
          ))}
          {!shown.length && <div className="empty-state"><p>{docs.length ? 'Ничего не найдено' : 'Правовая база пуста'}</p></div>}
        </div>
      </div>
    </div>
  );
}
